import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, fileSrc } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  FileText, Camera, X, Loader2, Send, Trash2, Download, Sparkles, Plus, Users, Cloud, Clock,
  UserPlus, Building2, Check, Image as ImageIcon, Eye, EyeOff,
} from "lucide-react";

const inputCls = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none transition";
const labelCls = "block text-[10px] font-bold uppercase text-slate-500 mb-1";

/**
 * DailyReports — reusable list + composer that lives inside a Ticket or
 * Project detail. AI generates a client-facing narrative from raw notes +
 * photos, WeasyPrint renders a PDF, and the report is stored per-date and
 * optionally emailed to recipients.
 *
 * Props: exactly one of { ticketId, projectId }. `onChange` (optional) fires
 * after each successful create/delete so the parent can refresh totals.
 */
export function DailyReports({ ticketId, projectId, onChange }) {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composerOpen, setComposerOpen] = useState(false);
  // Only office roles (never client nor technician) may toggle the
  // "share with client" flag — matches the business rule from the PDF.
  const canToggleVisibility = !["client", "technician"].includes(user?.role);
  const isClient = user?.role === "client";

  const load = () => {
    setLoading(true);
    const q = ticketId ? `ticket_id=${ticketId}` : `project_id=${projectId}`;
    api.get(`/daily-reports?${q}`)
      .then((rows) => setReports(rows || []))
      .catch(() => setReports([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [ticketId, projectId]);

  const remove = async (id) => {
    if (!window.confirm("Delete this daily report?")) return;
    try {
      await api.del(`/daily-reports/${id}`);
      toast.success("Report deleted");
      load();
      onChange && onChange();
    } catch { toast.error("Failed to delete"); }
  };

  const toggleVisibility = async (r) => {
    try {
      const next = !r.visible_to_client;
      await api.patch(`/daily-reports/${r.id}/visibility`, { visible_to_client: next });
      setReports((prev) => prev.map((x) => x.id === r.id ? { ...x, visible_to_client: next } : x));
      toast.success(next ? "Now visible to client" : "Hidden from client");
    } catch { toast.error("Failed to update"); }
  };

  return (
    <div className="border-t border-slate-200 pt-4" data-testid="daily-reports-section">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-slate-500" />
          <span className="text-xs font-bold uppercase tracking-wide text-slate-500">Daily reports</span>
        </div>
        <button
          data-testid="daily-report-new"
          onClick={() => setComposerOpen(true)}
          className={`inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-slate-900 rounded-lg px-3 py-1.5 hover:bg-slate-800 ${isClient ? "hidden" : ""}`}
        >
          <Plus className="w-3.5 h-3.5" /> Daily Report
        </button>
      </div>

      {loading ? (
        <div className="text-xs text-slate-400 py-2"><Loader2 className="w-3.5 h-3.5 inline animate-spin" /> Loading…</div>
      ) : reports.length === 0 ? (
        <p className="text-xs text-slate-400 py-1">No reports yet.           Click &quot;Daily Report&quot; to create the first one.</p>
      ) : (
        <div className="space-y-2">
          {reports.map((r) => (
            <div key={r.id}
                 data-testid={`daily-report-${r.id}`}
                 className="bg-white border border-slate-200 rounded-lg p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-900 truncate">{r.title}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                    <span className="tabular-nums">{r.report_date}</span>
                    <span>By {r.author_name || r.author_id?.slice(0, 6)}</span>
                    {r.crew?.length > 0 && (
                      <span className="inline-flex items-center gap-0.5">
                        <Users className="w-3 h-3" />
                        {r.crew.map((c) => (typeof c === "string" ? c : (c.name + (c.hours ? ` (${c.hours}h)` : "")))).join(", ")}
                      </span>
                    )}
                    {r.subcontractors?.length > 0 && (
                      <span className="inline-flex items-center gap-0.5">
                        <Users className="w-3 h-3" />
                        Subs: {r.subcontractors.map((s) => s.name + (s.hours ? ` (${s.hours}h)` : "")).join(", ")}
                      </span>
                    )}
                    {r.hours_worked != null && <span className="inline-flex items-center gap-0.5"><Clock className="w-3 h-3" />{r.hours_worked}h</span>}
                    {r.weather && <span className="inline-flex items-center gap-0.5"><Cloud className="w-3 h-3" />{r.weather}</span>}
                    {r.photos?.length > 0 && <span>{r.photos.length} photo{r.photos.length > 1 ? "s" : ""}</span>}
                    {r.sent_at && <span className="text-emerald-600 font-semibold">✓ Sent</span>}
                  </div>
                  {r.narrative && (
                    <p className="text-[11px] text-slate-600 mt-2 line-clamp-3">{r.narrative}</p>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {canToggleVisibility && (
                    <button
                      data-testid={`daily-report-visibility-${r.id}`}
                      onClick={() => toggleVisibility(r)}
                      title={r.visible_to_client ? "Visible to client — click to hide" : "Hidden from client — click to share"}
                      className={`p-1.5 rounded ${r.visible_to_client ? "text-emerald-600 bg-emerald-50 hover:bg-emerald-100" : "text-slate-400 hover:bg-slate-100"}`}
                    >
                      {r.visible_to_client ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </button>
                  )}
                  {isClient && r.visible_to_client && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 rounded px-1.5 py-0.5" title="Shared with you">
                      <Eye className="w-3 h-3" /> Shared
                    </span>
                  )}
                  <a
                    href={fileSrc(r.pdf_url)}
                    target="_blank" rel="noreferrer"
                    data-testid={`daily-report-download-${r.id}`}
                    title="Download PDF"
                    className="p-1.5 rounded text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                  ><Download className="w-4 h-4" /></a>
                  {!isClient && (
                    <button
                      data-testid={`daily-report-delete-${r.id}`}
                      onClick={() => remove(r.id)}
                      title="Delete"
                      className="p-1.5 rounded text-slate-400 hover:bg-red-50 hover:text-red-600"
                    ><Trash2 className="w-4 h-4" /></button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {composerOpen && (
        <DailyReportComposer
          ticketId={ticketId} projectId={projectId}
          onClose={() => setComposerOpen(false)}
          onDone={() => { setComposerOpen(false); load(); onChange && onChange(); }}
        />
      )}
    </div>
  );
}

function DailyReportComposer({ ticketId, projectId, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [photos, setPhotos] = useState([]);   // [{ id, url, filename }]
  const [uploading, setUploading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [subcontractors, setSubcontractors] = useState([]);
  const [crew, setCrew] = useState([]);            // [{ name, hours, tech_id }]
  const [subs, setSubs] = useState([]);            // [{ name, hours, workers, supplier_id }]
  const [form, setForm] = useState({
    title: "",
    report_date: new Date().toISOString().slice(0, 10),
    notes: "",
    weather: "",
    recipients: "",
    skip_ai: false,
    visible_to_client: false,
  });

  // Load employee + subcontractor catalogs once.
  // We call `/daily-reports/crew-candidates` (auth-only) so technicians —
  // who don't have permission on `/users` — can still see their coworkers.
  useEffect(() => {
    api.get("/daily-reports/crew-candidates")
      .then((rows) => setEmployees(rows || []))
      .catch(() => setEmployees([]));
    api.get("/subcontractors").then((rows) => setSubcontractors(rows || [])).catch(() => setSubcontractors([]));
  }, []);

  // Auto-fill time-clock hours for each selected crew member whenever the date
  // changes or a crew member is added. Fires a single aggregated call.
  useEffect(() => {
    const techIds = crew.map((c) => c.tech_id).filter(Boolean);
    if (techIds.length === 0) return;
    api.get(`/timeclock/hours-by-day?date=${form.report_date}&tech_ids=${techIds.join(",")}`)
      .then((rows) => {
        const byId = Object.fromEntries((rows || []).map((r) => [r.tech_id, r.hours]));
        setCrew((prev) => prev.map((c) => c.tech_id ? { ...c, hours: byId[c.tech_id] ?? c.hours } : c));
      })
      .catch(() => {});
    // eslint-disable-next-line
  }, [form.report_date, crew.map((c) => c.tech_id).join(",")]);

  // Downscale + re-encode a photo before upload so a 6 MB HEIC/JPG from a
  // phone camera doesn't push the /daily-reports request past the Cloudflare
  // proxy limits and OOM the PDF renderer in production.
  const shrinkPhoto = (file) => new Promise((resolve) => {
    if (!file || !file.type?.startsWith("image/")) return resolve(file);
    const img = new Image();
    img.onload = () => {
      const MAX = 1600; // longest edge in px
      let { width, height } = img;
      if (width > MAX || height > MAX) {
        const ratio = Math.min(MAX / width, MAX / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      canvas.toBlob((blob) => {
        if (!blob) return resolve(file);
        // Preserve the original name but force .jpg extension for consistency
        const stem = file.name.replace(/\.[^.]+$/, "") || "photo";
        resolve(new File([blob], `${stem}.jpg`, { type: "image/jpeg" }));
      }, "image/jpeg", 0.82);
    };
    img.onerror = () => resolve(file);
    img.src = URL.createObjectURL(file);
  });

  const onFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const uploaded = [];
    for (const raw of Array.from(files)) {
      try {
        const file = await shrinkPhoto(raw);
        const res = await api.upload(file);
        uploaded.push({ id: res.id, url: res.url, filename: res.filename });
      } catch (e) {
        toast.error(`Failed to upload ${raw.name}`);
      }
    }
    setPhotos((p) => [...p, ...uploaded]);
    setUploading(false);
  };

  const submit = async () => {
    if (!form.notes.trim()) { toast.error("Notes are required"); return; }
    setBusy(true);
    try {
      const payload = {
        ticket_id: ticketId || null,
        project_id: projectId || null,
        title: form.title || null,
        report_date: form.report_date,
        notes: form.notes,
        photos: photos.map((p) => p.id),
        crew: crew.filter((c) => c.name).map((c) => ({
          name: c.name,
          hours: c.hours != null ? Number(c.hours) : null,
          tech_id: c.tech_id || null,
        })),
        subcontractors: subs.filter((s) => s.name).map((s) => ({
          name: s.name,
          hours: s.hours != null && s.hours !== "" ? Number(s.hours) : null,
          workers: s.workers != null && s.workers !== "" ? Number(s.workers) : null,
          supplier_id: s.supplier_id || null,
        })),
        hours_worked: null,
        weather: form.weather || "",
        recipients: form.recipients ? form.recipients.split(",").map((s) => s.trim()).filter(Boolean) : [],
        skip_ai: form.skip_ai,
        visible_to_client: !!form.visible_to_client,
      };
      const res = await api.post("/daily-reports", payload);
      toast.success(res.sent_at ? "Report generated & sent" : "Report generated");
      onDone();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to create report");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[92vh] overflow-y-auto shadow-2xl"
           onClick={(e) => e.stopPropagation()}
           data-testid="daily-report-composer">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-base font-semibold inline-flex items-center gap-2"><FileText className="w-4 h-4" /> New Daily Report</h2>
            <div className="text-[11px] text-slate-500 mt-0.5">AI turns your notes + photos into a client-ready PDF.</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <div className="md:col-span-2">
              <label className={labelCls}>Title (optional)</label>
              <input type="text" value={form.title}
                data-testid="dr-title"
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Framing progress — Day 3"
                className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Date</label>
              <input type="date" value={form.report_date}
                onChange={(e) => setForm({ ...form, report_date: e.target.value })}
                className={inputCls} />
            </div>
          </div>

          <div>
            <label className={labelCls}>Notes *</label>
            <textarea rows={5} value={form.notes}
              data-testid="dr-notes"
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="What was done today? Any issues? Materials used? Next steps?"
              className={inputCls} />
            <label className="mt-1.5 inline-flex items-center gap-1.5 text-[10px] text-slate-500 cursor-pointer">
              <input type="checkbox" checked={form.skip_ai}
                onChange={(e) => setForm({ ...form, skip_ai: e.target.checked })} />
              Skip AI rewrite — save notes exactly as typed
            </label>
            <label className="mt-1.5 inline-flex items-center gap-2 text-xs cursor-pointer bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
              <input
                type="checkbox"
                data-testid="dr-visible-to-client"
                checked={form.visible_to_client}
                onChange={(e) => setForm({ ...form, visible_to_client: e.target.checked })}
              />
              <Eye className="w-3.5 h-3.5 text-emerald-700" />
              <span className="font-semibold text-emerald-800">Share with client</span>
              <span className="text-[10px] text-emerald-700/70">— they will see this report in their portal</span>
            </label>
          </div>

          {/* Crew — employee picker with time-clock hours auto-fill */}
          <CrewPicker
            employees={employees}
            crew={crew} setCrew={setCrew}
            reportDate={form.report_date}
          />

          {/* Subcontractors on-site */}
          <SubcontractorPicker
            subcontractors={subcontractors}
            subs={subs} setSubs={setSubs}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div>
              <label className={labelCls}>Weather</label>
              <input type="text" value={form.weather}
                onChange={(e) => setForm({ ...form, weather: e.target.value })}
                placeholder="sunny 72F"
                className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Email recipients (comma-separated)</label>
              <input type="text" value={form.recipients}
                data-testid="dr-recipients"
                onChange={(e) => setForm({ ...form, recipients: e.target.value })}
                placeholder="client@company.com, pm@..."
                className={inputCls} />
            </div>
          </div>

          <div>
            <label className={labelCls}>Photos ({photos.length})</label>
            <div className="flex flex-wrap gap-2">
              {photos.map((p) => (
                <div key={p.id} className="relative">
                  <img src={fileSrc(p.url)} alt="" className="w-20 h-20 object-cover rounded-lg border border-slate-200" />
                  <button
                    type="button"
                    onClick={() => setPhotos(photos.filter((x) => x.id !== p.id))}
                    className="absolute -top-2 -right-2 bg-white border border-slate-300 rounded-full p-0.5 hover:bg-rose-50"
                  ><X className="w-3 h-3" /></button>
                </div>
              ))}
              {/* Take photo — opens native camera */}
              <label className="w-20 h-20 flex flex-col items-center justify-center border-2 border-dashed border-slate-300 rounded-lg text-slate-500 hover:border-slate-500 hover:text-slate-800 cursor-pointer"
                     title="Take photo with camera">
                {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}
                <span className="text-[10px] mt-0.5">Camera</span>
                <input type="file" accept="image/*" capture="environment" multiple
                  data-testid="dr-photo-camera"
                  onChange={(e) => onFiles(e.target.files)}
                  className="hidden" />
              </label>
              {/* Choose from library — no capture attribute so mobile shows the photo picker */}
              <label className="w-20 h-20 flex flex-col items-center justify-center border-2 border-dashed border-slate-300 rounded-lg text-slate-500 hover:border-slate-500 hover:text-slate-800 cursor-pointer"
                     title="Pick from library">
                {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImageIcon className="w-5 h-5" />}
                <span className="text-[10px] mt-0.5">Library</span>
                <input type="file" accept="image/*" multiple
                  data-testid="dr-photo-library"
                  onChange={(e) => onFiles(e.target.files)}
                  className="hidden" />
              </label>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50 rounded-b-xl sticky bottom-0">
          <button onClick={onClose} disabled={busy}
            className="text-xs px-3 py-1.5 rounded border border-slate-300 hover:bg-white">Cancel</button>
          <button
            data-testid="dr-generate"
            onClick={submit}
            disabled={busy}
            className="text-xs px-4 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-bold disabled:opacity-50 inline-flex items-center gap-1"
          >{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : form.skip_ai ? <Send className="w-3.5 h-3.5" /> : <Sparkles className="w-3.5 h-3.5" />}
            {busy ? "Generating..." : form.skip_ai ? "Save & PDF" : "Generate with AI & PDF"}</button>
        </div>
      </div>
    </div>
  );
}

/* ----------------------------- CrewPicker ------------------------------- */
function CrewPicker({ employees, crew, setCrew, reportDate }) {
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    window.addEventListener("mousedown", h);
    return () => window.removeEventListener("mousedown", h);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const already = new Set(crew.map((c) => c.tech_id).filter(Boolean));
    return employees
      .filter((u) => !already.has(u.id))
      .filter((u) => !q || (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q))
      .slice(0, 8);
  }, [query, employees, crew]);

  const add = (u) => {
    setCrew([...crew, { name: u.name, tech_id: u.id, hours: null }]);
    setQuery("");
    setOpen(false);
  };
  const addFreeText = () => {
    const name = query.trim();
    if (!name) return;
    setCrew([...crew, { name, tech_id: null, hours: null }]);
    setQuery("");
    setOpen(false);
  };

  return (
    <div ref={wrapRef}>
      <label className={labelCls + " flex items-center gap-1.5"}>
        <Users className="w-3 h-3" /> Crew on-site ({crew.length})
        <span className="text-[9px] font-normal text-slate-400 normal-case">— hours auto-filled from time clock ({reportDate})</span>
      </label>
      <div className="relative">
        <input type="text" value={query}
          data-testid="dr-crew-search"
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search employee by name…"
          className={inputCls} />
        {open && (filtered.length > 0 || query.trim()) && (
          <div className="absolute z-30 mt-1 left-0 right-0 bg-white border border-slate-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
            {filtered.map((u) => (
              <button type="button" key={u.id}
                onClick={() => add(u)}
                data-testid={`dr-crew-opt-${u.id}`}
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-100 inline-flex items-center gap-2">
                <UserPlus className="w-3.5 h-3.5 text-slate-400" />
                <span className="truncate">{u.name}</span>
                <span className="text-[10px] text-slate-400 ml-auto uppercase">{u.role}</span>
              </button>
            ))}
            {query.trim() && (
              <button type="button" onClick={addFreeText}
                className="w-full text-left px-3 py-2 text-sm border-t border-slate-100 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 font-semibold inline-flex items-center gap-1.5">
                <Plus className="w-3.5 h-3.5" /> Add &ldquo;{query.trim()}&rdquo; as free text
              </button>
            )}
          </div>
        )}
      </div>
      {crew.length > 0 && (
        <div className="mt-2 space-y-1">
          {crew.map((c, i) => (
            <div key={i} className="flex items-center gap-2 px-2 py-1 rounded border border-slate-200 bg-slate-50">
              <UserPlus className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <span className="text-xs font-medium text-slate-800 flex-1 truncate">{c.name}</span>
              {c.tech_id && <Check className="w-3 h-3 text-emerald-600" title="Time clock linked" />}
              <div className="relative">
                <input type="text" inputMode="decimal"
                  value={c.hours ?? ""}
                  onChange={(e) => setCrew(crew.map((x, j) => j === i ? { ...x, hours: e.target.value.replace(/[^0-9.]/g, "") } : x))}
                  placeholder="0"
                  className="w-16 text-xs rounded border border-slate-300 px-2 py-0.5 text-right tabular-nums" />
                <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[9px] text-slate-400 pointer-events-none">h</span>
              </div>
              <button onClick={() => setCrew(crew.filter((_, j) => j !== i))}
                className="text-slate-400 hover:text-rose-600">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* -------------------------- SubcontractorPicker ------------------------- */
function SubcontractorPicker({ subcontractors, subs, setSubs }) {
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    window.addEventListener("mousedown", h);
    return () => window.removeEventListener("mousedown", h);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const already = new Set(subs.map((s) => s.supplier_id).filter(Boolean));
    return subcontractors
      .filter((s) => !already.has(s.id))
      .filter((s) => !q || (s.name || "").toLowerCase().includes(q))
      .slice(0, 8);
  }, [query, subcontractors, subs]);

  const add = (sc) => {
    setSubs([...subs, { name: sc.name, supplier_id: sc.id, hours: null, workers: null }]);
    setQuery(""); setOpen(false);
  };
  const addFree = () => {
    const name = query.trim();
    if (!name) return;
    setSubs([...subs, { name, supplier_id: null, hours: null, workers: null }]);
    setQuery(""); setOpen(false);
  };

  return (
    <div ref={wrapRef}>
      <label className={labelCls + " flex items-center gap-1.5"}>
        <Building2 className="w-3 h-3" /> Subcontractors on-site ({subs.length})
      </label>
      <div className="relative">
        <input type="text" value={query}
          data-testid="dr-sub-search"
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search subcontractor…"
          className={inputCls} />
        {open && (filtered.length > 0 || query.trim()) && (
          <div className="absolute z-30 mt-1 left-0 right-0 bg-white border border-slate-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
            {filtered.map((sc) => (
              <button type="button" key={sc.id}
                onClick={() => add(sc)}
                data-testid={`dr-sub-opt-${sc.id}`}
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-100 inline-flex items-center gap-2">
                <Building2 className="w-3.5 h-3.5 text-slate-400" />
                <span className="truncate">{sc.name}</span>
                {sc.trade && <span className="text-[10px] text-slate-400 ml-auto">{sc.trade}</span>}
              </button>
            ))}
            {query.trim() && (
              <button type="button" onClick={addFree}
                className="w-full text-left px-3 py-2 text-sm border-t border-slate-100 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 font-semibold inline-flex items-center gap-1.5">
                <Plus className="w-3.5 h-3.5" /> Add &ldquo;{query.trim()}&rdquo; as free text
              </button>
            )}
          </div>
        )}
      </div>
      {subs.length > 0 && (
        <div className="mt-2 space-y-1">
          {subs.map((s, i) => (
            <div key={i} className="flex items-center gap-2 px-2 py-1 rounded border border-slate-200 bg-slate-50">
              <Building2 className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <span className="text-xs font-medium text-slate-800 flex-1 truncate">{s.name}</span>
              <div className="relative">
                <input type="text" inputMode="decimal"
                  value={s.workers ?? ""}
                  onChange={(e) => setSubs(subs.map((x, j) => j === i ? { ...x, workers: e.target.value.replace(/[^0-9]/g, "") } : x))}
                  placeholder="0"
                  title="Head count"
                  className="w-12 text-xs rounded border border-slate-300 px-2 py-0.5 text-right tabular-nums" />
                <span className="absolute right-1 top-1/2 -translate-y-1/2 text-[9px] text-slate-400 pointer-events-none">👤</span>
              </div>
              <div className="relative">
                <input type="text" inputMode="decimal"
                  value={s.hours ?? ""}
                  onChange={(e) => setSubs(subs.map((x, j) => j === i ? { ...x, hours: e.target.value.replace(/[^0-9.]/g, "") } : x))}
                  placeholder="0"
                  className="w-16 text-xs rounded border border-slate-300 px-2 py-0.5 text-right tabular-nums" />
                <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[9px] text-slate-400 pointer-events-none">h</span>
              </div>
              <button onClick={() => setSubs(subs.filter((_, j) => j !== i))}
                className="text-slate-400 hover:text-rose-600">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default DailyReports;
